// @flow
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import {
  get,
  flatMap,
  flow,
  head,
  intersection,
  map,
  find,
  filter,
  sortBy,
  some,
  keys,
  size,
  values
} from 'lodash/fp';

import {
  addEntity,
  autoFillLineUp,
  toggleEntityProperty,
  lockEntity,
  removeEntity,
  resetChallenge,
  resetLineUp,
  togglePositionFilter,
  switchSelectedPosition,
  unlockEntity,
  updateEntityProperty
} from 'app/actions/challengeActions';
import Message from 'app/components/commons/Message';
import { setSortField } from 'app/actions/playerListActions';
import entitiesSelector from 'app/selectors/entitiesSelector';
import positionsSelector from 'app/selectors/positionsSelector';
import salaryExpenditureSelector from 'app/selectors/salaryExpenditureSelector';
import canCompleteLineUpSelector from 'app/selectors/canCompleteLineUpSelector';

import PlayerSelection from './PlayerSelection';
import LineUp from './LineUp';

type Props = {
  autoAdvancePositions: boolean,
  maxExposurePerPlayer?: number,
  canCompleteTeam: boolean,
  entities: Array<Object>,
  filteredEntities: Array<Object>,
  isCrunching: boolean,
  keepCurrentLineUps: boolean,
  onAdd: () => void,
  onRemove: () => void,
  onResetChallenge: () => void,
  onUpdateEntityProperty: () => void,
  onPositionSelect: (pos: string, exclusive?: boolean) => void,
  onSetSortField: (field: string) => void,
  onSelectSettingsTab: (tab: string) => void,
  requiredSelections: number,
  salaryCap?: number,
  numLocked: number,
  numSelected: number,
  salaryExpenditure: number,
  sportHasTeam?: boolean,
  useAverage?: boolean,
  searchTerm?: string,
  onCrunchTeams: (players: Array<Object>) => void,
  onToggleKeepCurrentLineUps: () => void,
  onClear: () => void,
  onLock: () => void,
  positionFilters?: Array<string>,
  onUnlock: () => void,
  onToggleEntityProperty: () => void,
  onSwitchSelectedPosition: () => void,
  positions: Array<Object>,
  isCaptainContest?: boolean
};

const defaultProps = {
  maxExposurePerPlayer: undefined,
  salaryCap: undefined,
  sportHasTeam: true,
  useAverage: false,
  positionFilters: [],
  searchTerm: undefined,
  isCaptainContest: false
};

const Wrapper = styled.div(({ theme }) => ({
  marginBottom: theme.spacing.small
}));

const LineUpBuilder = ({
  autoAdvancePositions,
  canCompleteTeam,
  entities,
  keepCurrentLineUps,
  maxExposurePerPlayer,
  onAdd,
  onClear,
  onLock,
  onResetChallenge,
  isCrunching,
  onSelectSettingsTab,
  onRemove,
  positionFilters,
  onPositionSelect,
  onToggleEntityProperty,
  onCrunchTeams,
  onToggleKeepCurrentLineUps,
  onUnlock,
  onUpdateEntityProperty,
  onSetSortField,
  onSwitchSelectedPosition,
  positions,
  filteredEntities,
  salaryCap,
  searchTerm,
  salaryExpenditure,
  sportHasTeam,
  useAverage,
  requiredSelections,
  numLocked,
  numSelected,
  isCaptainContest
}: Props) => {
  const handleSubmitAll = () => onCrunchTeams(entities.filter(p => !!p.insiderId && !p.isExcluded));

  const handleSubmit = () => onCrunchTeams(filter('isFavorite', entities));

  const handleReset = () => {
    onSetSortField('insiderScoreUpdated');

    return () => {
      onResetChallenge();
      onClear();
    };
  };

  useEffect(handleReset, []);

  if (entities.length === 0) {
    return <Message kind="info">No players available.</Message>;
  }

  const handlePositionSelect = pos => {
    // captain position cannot be selected at he same time with others
    const exclusiveSelect = pos === 'captain' || positionFilters.includes('captain');
    onPositionSelect(pos, exclusiveSelect);
  };

  return (
    <Wrapper>
      <div className="columns is-desktop">
        <div className="column is-two-thirds-desktop">
          <PlayerSelection
            autoAdvancePositions={autoAdvancePositions}
            hasSearch
            lastUpdatedAt={get('insiderLastUpdatedAt', head(filteredEntities))}
            onAdd={onAdd}
            onToggleEntityProperty={onToggleEntityProperty}
            onUpdateEntityProperty={onUpdateEntityProperty}
            onSelectSettingsTab={onSelectSettingsTab}
            onPositionSelect={handlePositionSelect}
            onRemove={onRemove}
            players={sortBy(({ isFavorite }) => !isFavorite, filteredEntities)}
            positionFilters={positionFilters}
            positions={positions}
            requiredSelections={requiredSelections}
            salaryCap={salaryCap}
            salaryExpenditure={salaryExpenditure}
            searchTerm={searchTerm}
            sportHasTeam={sportHasTeam}
            useAverage={useAverage}
            totalSelected={numSelected}
            maxExposurePerPlayer={maxExposurePerPlayer}
            isCaptainContest={isCaptainContest}
          />
        </div>
        <div className="column is-one-third-desktop">
          <LineUp
            canCompleteTeam={canCompleteTeam}
            numLocked={numLocked}
            numSelected={numSelected}
            onClear={onClear}
            onLock={onLock}
            onSwitchSelectedPosition={onSwitchSelectedPosition}
            onRemove={onRemove}
            onSelectSettingsTab={onSelectSettingsTab}
            onToggleKeepCurrentLineUps={onToggleKeepCurrentLineUps}
            onUnlock={onUnlock}
            positions={positions}
            keepCurrentLineUps={keepCurrentLineUps}
            isCrunching={isCrunching}
            requiredSelections={requiredSelections}
            salaryCap={salaryCap}
            salaryExpenditure={salaryExpenditure}
            onSubmit={handleSubmit}
            onSubmitAll={handleSubmitAll}
          />
        </div>
      </div>
    </Wrapper>
  );
};

function mapStateToProps(state: GlobalState) {
  const { challenge, entry, lineUp, playerList, settings, user } = state;
  const selections = flow(flatMap(values))(lineUp.selections);
  // const e1 = performance.now();
  const entities = entitiesSelector(state);
  // console.log('PERF: entities', performance.now() - e1);
  // const p1 = performance.now();
  const positions = positionsSelector(state);
  // console.log('PERF: positions', performance.now() - p1);

  return {
    autoAdvancePositions: playerList.autoAdvancePositions,
    canCompleteTeam: canCompleteLineUpSelector(state),
    challengeId: challenge.id,
    cost: challenge.reducedCost,
    currencyCode: state.settings.currency.code,
    displayWidget: settings.displayWidget,
    hasError: lineUp.errorMessages.length > 0,
    hasEmbeddedUserEntry: !!entry.myEntry.id,
    gameFilter: lineUp.gameFilter,
    isH2H: challenge.isH2H,
    isLocked: challenge.isLocked,
    isRacing: challenge.isRacing,
    isRapidfire: challenge.isRapidfire,
    isSignedIn: user.isSignedIn,
    isSubmitting: lineUp.isSubmitting,
    isUpdating: lineUp.isUpdating,
    numLocked: size(filter('locked', selections)),
    numSelected: size(selections),
    filteredEntities: entities.filter(p => !!p.insiderId),
    positionFilters: lineUp.positionFilters,
    positions,
    playerSelectionTableInfo: challenge.playerSelectionTableInfo,
    requiredSelections: challenge.playerQuota + challenge.teamQuota,
    salaryCap: get('fantasyInsider.settings.salaryCap', state) || challenge.salaryCap,
    salaryExpenditure: salaryExpenditureSelector(state),
    searchTerm: playerList.searchTerm,
    selections: flow(flatMap(keys))(lineUp.selections),
    showPlayerSelections: challenge.showPlayerSelections,
    siteName: settings.siteName,
    sportName: challenge.sportName,
    startTime: challenge.startTime,
    name: challenge.name,
    statusId: challenge.statusId,
    pollTimestamp: challenge.pollTimestamp,
    maxExposurePerPlayer: get('fantasyInsider.settings.maxExposurePerPlayer', state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onAutoFill() {
      dispatch(autoFillLineUp());
    },
    onAdd(entity, position) {
      dispatch(addEntity(entity, position));
    },
    onClear() {
      dispatch(resetLineUp());
    },
    onLock(id, position) {
      dispatch(lockEntity(id, position));
    },
    onResetChallenge() {
      dispatch(resetChallenge());
    },
    onUpdateEntityProperty(id, field, value) {
      dispatch(updateEntityProperty(id, field, value));
    },
    onToggleEntityProperty(id, field) {
      dispatch(toggleEntityProperty(id, field));
    },
    onPositionSelect(pos, exclusive) {
      dispatch(togglePositionFilter(pos, exclusive));
    },
    onRemove(id, position, switchPosition) {
      dispatch(removeEntity(id, position, switchPosition));
    },
    onSwitchSelectedPosition(id, position, newPosition) {
      dispatch(switchSelectedPosition(id, position, newPosition));
    },
    onUnlock(id, position) {
      dispatch(unlockEntity(id, position));
    },
    onSetSortField(field) {
      dispatch(setSortField(field));
    }
  };
}

// order a player's positions by the position order of the challenge
function orderByPositions(playerPosIds, positions) {
  const posIds = map('id')(positions);
  return intersection(playerPosIds)(posIds);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  const { positions } = stateProps;
  const { onAdd } = dispatchProps;
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    isMultiEntry: ownProps.numChallengesToCreate ? ownProps.numChallengesToCreate > 1 : false,
    onAdd(entity, playerPositions, isCaptain) {
      const posKeys = map('key')(playerPositions);

      let sortedPosKeys = orderByPositions(posKeys, positions);

      // if a captain position is available and it's not filtered by captain position remove it from the pool of positions
      if (!isCaptain && sortedPosKeys.includes('captain'))
        sortedPosKeys = sortedPosKeys.filter(p => p !== 'captain');

      // if is filtering by captain position allow to add captain only
      if (isCaptain) sortedPosKeys = ['captain'];

      const pos = find(
        key =>
          some(
            {
              id: key,
              filled: false
            },
            positions
          ),
        sortedPosKeys
      );
      onAdd(entity, pos);
    }
  };
}

LineUpBuilder.defaultProps = defaultProps;

export default React.memo(connect(mapStateToProps, mapDispatchToProps, mergeProps)(LineUpBuilder));
